import { FC } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import type { GrupoRelatorios, ItemRelatorio } from '@/config/relatorios';
import { buscaDoFiltro } from '@/utils/filtroPaineis';

interface Props {
  grupos: GrupoRelatorios[];
  /** Fecha a gaveta no mobile depois de navegar. */
  onNavegar?: () => void;
}

const BASE_ITEM =
  'flex items-start gap-2.5 rounded-lg px-3 py-2 text-sm transition-colors';

const Icone: FC<{ nome: string }> = ({ nome }) => (
  <span aria-hidden className="material-symbols-outlined mt-px text-[18px] leading-none">
    {nome}
  </span>
);

/** Item já combinado e ainda não publicado: aparece apagado e sem link. */
const ItemEmBreve: FC<{ item: ItemRelatorio }> = ({ item }) => (
  <li>
    <span className={`${BASE_ITEM} cursor-default text-content-muted`} aria-disabled="true">
      <Icone nome={item.icone} />
      <span className="min-w-0">
        {item.rotulo}
        <span className="ml-1.5 inline-block whitespace-nowrap rounded bg-surface-tertiary px-1.5 py-0.5 text-2xs uppercase tracking-wide">
          em breve
        </span>
      </span>
    </span>
  </li>
);

/** Os painéis compartilham o filtro de período e produto: o link leva o filtro junto. */
const ItemLink: FC<{ item: ItemRelatorio; onNavegar?: () => void }> = ({ item, onNavegar }) => {
  const [busca] = useSearchParams();
  const destino = item.gate === 'dashboard' ? { pathname: item.rota, search: buscaDoFiltro(busca.toString()) } : item.rota;
  return (
    <li>
      <NavLink
        to={destino}
        onClick={onNavegar}
        className={({ isActive }) =>
          `${BASE_ITEM} ${
            isActive
              ? 'bg-surface-hover font-semibold text-accent'
              : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary'
          }`
        }
      >
        <Icone nome={item.icone} />
        <span className="min-w-0">{item.rotulo}</span>
      </NavLink>
    </li>
  );
};

/**
 * Menu lateral dos relatórios.
 *
 * Substitui as abas que existiam no topo do painel da Equiplex: a navegação entre
 * todos os relatórios passa a acontecer num lugar só. A lista já chega filtrada
 * pelas flags do usuário (`useRelatorios`) — item que daria 403 não aparece.
 */
const RelatoriosMenu: FC<Props> = ({ grupos, onNavegar }) => (
  <nav aria-label="Relatórios" className="flex flex-col gap-5">
    <NavLink
      to="/relatorios"
      end
      onClick={onNavegar}
      className={({ isActive }) =>
        `${BASE_ITEM} ${
          isActive
            ? 'bg-surface-hover font-semibold text-accent'
            : 'text-content-secondary hover:bg-surface-hover hover:text-content-primary'
        }`
      }
    >
      <Icone nome="grid_view" />
      <span>Todos os relatórios</span>
    </NavLink>

    {grupos.map((grupo) => (
      <div key={grupo.id}>
        <h2 className="px-3 pb-1.5 text-2xs font-semibold uppercase tracking-wide text-content-muted">
          {grupo.titulo}
        </h2>
        <ul className="flex flex-col gap-0.5">
          {grupo.itens.map((item) =>
            item.emBreve ? (
              <ItemEmBreve key={item.rota} item={item} />
            ) : (
              <ItemLink key={item.rota} item={item} onNavegar={onNavegar} />
            ),
          )}
        </ul>
      </div>
    ))}
  </nav>
);

export default RelatoriosMenu;
